<template>
    <div v-if="loaded">
        <div>
            <template>
                <span class="pivot-title mr-3">{{title}}</span>
                <b-btn size="sm" :variant="settingsVisible ? 'primary' : 'secondary' " class="mb-1 mr-3"
                       @click="settingsVisible = !settingsVisible">
                    <fa icon="cogs" class="mr-1"/>{{ $t('sandoz.toggle_settings') }}
                </b-btn>
            </template>
            <template v-if="favourites && favourites.length > 0">
                <b-btn size="sm" class="mr-1 mb-1" v-for="(favorite, key) in favourites" :key="key + 0"
                       @click="filterByFavourite(favorite, key)" :variant="favouriteKey.includes(key)?'primary':'secondary'">
                    <fa icon="filter" class="mr-1"/>
                    {{ favorite.name ? favorite.name : $t('sandoz.toggle_settings', favorite.filters.join(', ')) | capitalize }}
                </b-btn>
                <b-btn size="sm" class="mt-1 mb-1 float-right" v-if="favouriteKey.length > 0" @click="resetFilter">
                    <fa icon="redo-alt" class="mr-1"/> {{ $t('sandoz.reset_filter') }}
                </b-btn>
            </template>
        </div>
        <o-card v-if="settingsVisible" class="mt-3">
            <b-row>
                <b-col>
                    <strong>{{ $t('sandoz.available_fields') }}</strong>
                    <draggable class="list-group mt-1" tag="ul" v-model="fields" @end="endDragRows" handle=".handle" group="columns">
                        <transition-group type="transition" :name="'flip-list'">
                            <li class="list-group-item p-1" v-for="(element, key) in fields" :key="key + 0">
                                <fa icon="align-justify" class="handle"/>
                                {{element | capitalize}}
                            </li>
                        </transition-group>
                    </draggable>
                </b-col>
                <b-col>
                    <strong>{{ $t('sandoz.filters') }}</strong>
                    <draggable class="dragArea list-group mt-1" tag="ul" v-model="pivotFilters" @start="isDragging=true" @end="endDragRows" handle=".handle" :group="{ name: 'columns', pull: false }">
                        <transition-group type="transition" :name="'flip-list'">
                            <li class="list-group-item p-1" v-for="(element, key) in pivotFilters" :key="key + 0">
                                <fa icon="align-justify" class="handle"/>
                                {{element | capitalize}}
                                <fa icon="times" class="float-right m-1 cursor-pointer" v-if="pivotFilters.length > 1" @click="remove(pivotFilters, key)"></fa>
                            </li>
                        </transition-group>
                    </draggable>
                </b-col>
                <b-col>
                    <strong>{{ $t('sandoz.rows') }}</strong>
                    <draggable class="dragArea list-group mt-1" tag="ul" v-model="pivotRows" @start="isDragging=true" @end="endDragRows" handle=".handle" :group="{ name: 'columns', pull: false }">
                        <transition-group type="transition" :name="'flip-list'">
                            <li class="list-group-item p-1" v-for="(element, key) in pivotRows" :key="key + 0">
                                <fa icon="align-justify" class="handle"/>
                                {{element | capitalize}}
                                <fa icon="times" class="float-right m-1 cursor-pointer" v-if="pivotRows.length > 1" @click="remove(pivotRows, key)"></fa>
                            </li>
                        </transition-group>
                    </draggable>
                </b-col>
                <b-col>
                    <strong>{{ $t('sandoz.columns') }}</strong>
                    <draggable class="list-group mt-1" tag="ul" v-model="pivotValues" @change="changed" @end="endDragRows" handle=".handle" :group="{ name: 'columns', pull: false}">
                        <transition-group type="transition" :name="'flip-list'">
                            <li class="list-group-item p-1 d-flex" v-for="(element, key) in pivotValues" :key="key + 0">
                                <div>
                                    <fa icon="align-justify" class="handle"/>
                                    {{element.field | capitalize}}
                                </div>

                                <select v-model="element.function" @change="processData" class="ml-auto">
                                    <option v-for="(availableFunction, key) in functions" :key="key">{{availableFunction}}</option>
                                </select>
                                <fa icon="times" class="float-right m-1 ml-3 cursor-pointer" v-if="pivotValues.length > 1"  @click="remove(pivotValues, key)"></fa>
                            </li>
                        </transition-group>
                    </draggable>
                </b-col>
            </b-row>
        </o-card>

        <o-card class="mt-3">
            <div class="d-flex">
                <b-button-toolbar v-if="pivotFilters.length > 0">
                    <b-button-group>
                        <template v-for="(filter) in selectedFilterValues">
                            <b-dropdown no-flip left :key="filter.field" :variant="filter.values.length == 0 ? 'outline-secondary': 'primary'" v-if="pivotFilters.includes(filter.field)" @hidden="filterHidden">
                                <template #button-content>
                                    {{filter.field | capitalize}}
                                </template>
                                <b-dropdown-form>
                                    <a href="javascript:void (0);" class="btn-link" @click="selectAll(filter.field)"
                                       v-if="filterValues[filter.field] && filterValues[filter.field].length > 10">
                                        {{ $t('sandoz.select_all') }}
                                    </a>
                                    <b-form-checkbox v-for="(filterValue, index) in (filterValues[filter.field] ? filterValues[filter.field].sort(): [])" :key="filter.field + '/' + index" :value="filterValue" v-model="filter.values">
                                        {{filterValue}}
                                    </b-form-checkbox>
                                </b-dropdown-form>
                            </b-dropdown>
                        </template>
                    </b-button-group>
                </b-button-toolbar>

                <b-btn @click="exportTable" class="ml-auto">{{ $t('sandoz.export') }}</b-btn>
            </div>

            <table class="table table-sm" ref="pivotTable">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-right" v-for="(value, key) in pivotValues" :key="key">{{value.field | capitalize}}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(value, propertyName, index) in parsedData">
                        <tree-table-row :formatNumbers="formatNumbers" :label="propertyName" :data="value" :key="index"></tree-table-row>
                    </template>
                </tbody>
            </table>
        </o-card>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import TreeTableRow from './TreeTableRow.vue'

export default {
    props: {
        statisticData: {},
        filters: {
            type: Array
        },
        rows: {
            type: Array
        },
        values: {
            type: Array
        },
        favourites: {
            type: Array
        },
        filterDefaultValues: {
            type: Array,
            default: () => []
        },
        title: {}
    },
    data: () => ({
        loaded: false,
        isDragging: false,
        delayedDragging: false,
        settingsVisible: false,
        fields: [],
        functions: ['count', 'count_unique', 'sum'],
        formatNumbers: true,
        parsedData: {},
        filterValues: {},
        selectedFilterValues: [],
        // used by count_unique to determine the already found values
        uniqueValues: {},
        filterUpdated: false,
        pivotFilters: [],
        pivotRows: [],
        pivotValues: [],
        favouriteKey: []
    }),

    computed: {
        statisticDataFiltered() {
            var activeFilters = this.selectedFilterValues.filter(i => i.values.length > 0)

            return this.statisticData.filter(e => {
                var found = true
                activeFilters.forEach(activeFilter => {
                    if (!activeFilter.values.includes(e[activeFilter.field])) {
                        found = false
                    }
                })
                return found
            })
        }
    },
    created () {
        this.pivotFilters = this.filters
        this.pivotRows = this.rows
        this.pivotValues = this.values
    },
    methods: {
        exportTable() {
            var currentNumberFormat = this.formatNumbers
            this.formatNumbers = false
            this.$nextTick(() => {
                this.tableToExcel(this.$refs.pivotTable)
            })
            this.$nextTick(() => {
                this.formatNumbers = currentNumberFormat
            })
        },
        tableToExcel(table) {
            var uri = 'data:application/vnd.ms-excel;base64,'
                , template = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><?xml version="1.0" encoding="UTF-8" standalone="yes"?><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>{table}</table></body></html>'
                , base64 = function(s) { return window.btoa(unescape(encodeURIComponent(s))) }
                , format = function(s, c) { return s.replace(/{(\w+)}/g, function(m, p) { return c[p]; }) }

            var ctx = { worksheet: name || 'Worksheet', table: table.innerHTML }
            window.location.href = uri + base64(format(template, ctx))
        },
        endDragRows() {
            this.isDragging = false
            this.processData()
        },
        cloneColumn(value) {
            return {
                function: '',
                field: value
            }
        },
        filterHidden() {
            // we don't want to reload the data right when activating a filter checkbox, otherwise
            // the drop down would immediately close. Instead we remember that we want to reload the
            // data and do it, once the dropdown is closed
            if (this.filterUpdated) {
                this.filterUpdated = false
                this.processData()
            }
        },
        processData () {
            console.time('start')
            const _self = this

            _self.loaded = false

            _self.parsedData = {}
            _self.uniqueValues = {}
            _self.fields = []

            if (_self.statisticData.length == 0) {
                _self.loaded = true
                return
            }

            // determine all available fields
            _self.fields = Object.keys(_self.statisticData[0])

            // prepare array to hold all possible filter values
            if (_self.selectedFilterValues.length == 0) {
                _self.pivotFilters.forEach(e => _self.filterValues[e] = [])
            }

            _self.statisticDataFiltered.forEach(r => {
                // add unique filter values
                _self.pivotFilters.forEach(e => {
                    if (!_self.filterValues[e]) {
                        _self.filterValues[e] = [r[e]]
                    } else if (_self.filterValues[e] && _self.filterValues[e].indexOf(r[e]) === -1) {
                        _self.filterValues[e].push(r[e])
                    }
                })

                // build data array
                var oldPointer = null
                var pointer = _self.parsedData
                var rowPath = ''
                _self.pivotRows.forEach(row => {
                    // build complete hierarchical path for row fields. We need this to store things per row like the
                    // unique values if we are using count_unique
                    rowPath += r[row] + '//'

                    if (!pointer.hasOwnProperty(r[row])) {
                        var newChild = {}
                        pointer[r[row]] = {
                            values: {},
                            children: newChild
                        }

                        oldPointer = pointer
                        pointer = newChild
                    }
                    else {
                        oldPointer = pointer
                        pointer = pointer[r[row]].children
                    }

                    // add values
                    _self.pivotValues.forEach((entry) => {
                        var pointerValues = oldPointer[r[row]].values
                        if (!pointerValues.hasOwnProperty(entry.field)) {
                            pointerValues[entry.field] = 0
                        }

                        switch (entry.function) {
                            case 'count':
                                pointerValues[entry.field]++
                                break;
                            case 'sum':
                                pointerValues[entry.field] += Number(r[entry.field])
                                break
                            case 'count_unique':
                                if (!_self.uniqueValues.hasOwnProperty(rowPath + '_' + entry.field)) {
                                    _self.uniqueValues[rowPath + '_' + entry.field] = []
                                }
                                if (_self.uniqueValues[rowPath + '_' + entry.field].indexOf(r[entry.field]) == -1) {
                                    _self.uniqueValues[rowPath + '_' + entry.field].push(r[entry.field])
                                }
                                pointerValues[entry.field] = _self.uniqueValues[rowPath + '_' + entry.field].length
                                break
                        }
                    })
                })
            })

            _self.$nextTick(() => {
                if(_self.selectedFilterValues.length == 0) {
                    if (_self.filterDefaultValues && _self.filterDefaultValues.length) {
                        _self.fields.forEach(function (e) {
                            let _value = []
                            _self.filterDefaultValues.forEach(function (filter) {
                                if(filter[e]) {
                                    _value = (typeof filter[e] === 'object') ? filter[e] : [filter[e]]
                                    return false
                                }
                            })
                            _self.selectedFilterValues.push({field: e, values: _value})
                        })
                        this.endDragRows()
                    } else {
                        _self.fields.forEach(e => _self.selectedFilterValues.push({field: e, values: []}))
                    }
                }

                _self.loaded = true
            })

            console.timeEnd('start')
        },
        remove: function (items, index) {
            if (items.length > 1) { // keep at least one value drag area
                items.splice(index, 1)
                this.processData()
            }
        },
        changed: function (evt) {
            this.pivotValues[evt.added.newIndex] = this.cloneColumn(evt.added.element)
        },
        filterByFavourite: function (favorite, key) {
            this.favouriteKey = []
            this.pivotFilters = favorite.filters
            this.pivotRows = favorite.rows
            this.pivotValues = favorite.values
            this.favouriteKey.push(key)
            this.endDragRows()
        },
        resetFilter: function () {
            this.pivotFilters = this.filters
            this.pivotRows = this.rows
            this.pivotValues = this.values
            this.favouriteKey = []
            this.endDragRows()
        },
        selectAll: function (field) {
            const _self = this
            _self.selectedFilterValues.forEach(function (filter, index) {
                if (field === filter.field) {
                    _self.filterValues[field].forEach(function (item) {
                        if (!_self.selectedFilterValues[index].values.includes(item)) {
                            _self.selectedFilterValues[index].values.push(item)
                        }
                    })
                    return false
                }
            })
        }
    },

    watch: {
        selectedFilterValues: {
            deep: true,
            handler() {
                if (!this.loaded) {
                    return
                }
                this.filterUpdated = true
            }
        },
        statisticData() {
            this.processData()
        }
    },

    components: {
        TreeTableRow,
        draggable
    },

}
</script>

<style>
.dropdown-menu {
    max-height: 70vh ! important;
    overflow: auto ! important;
}
</style>
